import React from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { getPostCover } from '@wix/communities-blog-client-common';

import { useSelector } from '../../../common/components/runtime-context';
import Banner from '../../components/banner';
import {
  getLastFeaturedPost,
  getLastFeaturedPostCategory,
} from '../../selectors/last-featured-post-selectors';
import styles from './featured-post-banner.scss';

/**
 * FeaturedPostBanner - A banner component that displays the latest featured blog post
 *
 * This component renders a prominent banner highlighting the most recent featured post
 * on Wix Blog sites. It displays the post title, category, cover image, and a read more link.
 * If no featured post is available, the component renders nothing.
 *
 * @component
 * @example
 * // Used on Wix Blog sites to highlight latest featured post:
 * // https://nl.wix.com/blog
 * // https://pl.wix.com/blog
 * // https://ru.wix.com/blog
 * // https://ja.wix.com/blog
 *
 * @returns Banner component or null if no featured post is available
 */

export const FeaturedPostBanner: React.FC = () => {
  const { t } = useTranslation();
  const lastFeaturedPost = useSelector(getLastFeaturedPost);
  const lastFeaturedPostCategory = useSelector(getLastFeaturedPostCategory);

  if (!lastFeaturedPost) {
    return null;
  }

  const category = lastFeaturedPostCategory?.label;
  const link = {
    to: `/${lastFeaturedPost.slug}`,
    text: t('banner.read-more-label'),
  };

  const { shouldRender, imageMetadata } = getPostCover(lastFeaturedPost);
  const image = shouldRender ? imageMetadata : null;

  return (
    <div className={styles.spacer}>
      <Banner title={lastFeaturedPost.title} category={category} link={link} image={image} />
    </div>
  );
};
